/**
 * AGENT BUILDER PAGE
 * 
 * Visual workflow editor for defining agent workflows.
 * n8n-style drag-and-drop interface for creating and editing workflows.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Play, FileText, ListTodo, Code, TestTube, Users, Bot, Rocket, CheckCircle,
  Plus, Trash2, Copy, Workflow as WorkflowIcon, Moon, Sun, X, Save, Loader2,
  Palette, ChevronsUpDown, BookOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useWorkflowStore,
  nodeTemplates,
  issueTypeConfig,
  providerModels,
  type Workflow,
  type WorkflowNode,
  type NodeType,
  type NodeConfig,
  type IssueType,
  type AIProvider,
} from "@/stores/workflowStore";
import { useUIStore } from "@/stores/uiStore";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import {
  getStartFieldLabel,
  getStartFieldOptions,
  getRequiredStartFields,
  type StartFieldKey,
} from "@/lib/workflow/startFields";

const nodeIcons: Record<NodeType, typeof Play> = {
  start: Play,
  plan: FileText,
  subtasks: ListTodo,
  ui_concept: Palette,
  code: Code,
  test: TestTube,
  human_review: Users,
  llm_review: Bot,
  deploy: Rocket,
  end: CheckCircle,
};

// ── AI Provider Config (shared by plan, subtasks, code, llm_review, test) ──

const AI_NODE_TYPES: NodeType[] = ["plan", "subtasks", "code", "llm_review", "test"];

function AIProviderConfig({
  config,
  onChange,
}: {
  config: NodeConfig;
  onChange: (updates: NodeConfig) => void;
}) {
  const provider = (config.provider || "claude") as AIProvider;
  const models = providerModels[provider]?.models || [];
  const customModelValue = "__custom__";
  const configuredModel = (config.model || "").trim();
  const hasConfiguredModel = configuredModel.length > 0;
  const hasPresetModel = models.some((m) => m.value === configuredModel);
  const selectModelValue = hasPresetModel
    ? configuredModel
    : hasConfiguredModel
      ? customModelValue
      : (models[0]?.value || customModelValue);

  const customModelPlaceholder =
    provider === "claude"
      ? "e.g. claude-opus-4-6"
      : provider === "codex"
        ? "e.g. gpt-5.3-codex"
        : "e.g. gemini-2.5-pro";
  const supportsReasoning = provider === "claude" || provider === "codex";
  const selectedReasoning = config.reasoningEffort || "high";

  return (
    <>
      <div>
        <Label>Provider</Label>
        <Select
          value={provider}
          onValueChange={(value: string) => {
            const newProvider = value as AIProvider;
            const firstModel = providerModels[newProvider]?.models[0]?.value;
            const nextConfig: NodeConfig = { ...config, provider: newProvider, model: firstModel };
            if (newProvider === "claude" || newProvider === "codex") {
              nextConfig.reasoningEffort = config.reasoningEffort || "high";
            } else {
              delete nextConfig.reasoningEffort;
            }
            onChange(nextConfig);
          }}
        >
          <SelectTrigger className="mt-1" data-testid="select-provider">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(providerModels) as AIProvider[]).map((key) => (
              <SelectItem key={key} value={key}>
                {providerModels[key].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Model</Label>
        <Select
          value={selectModelValue}
          onValueChange={(value: string) => {
            if (value === customModelValue) {
              onChange({ ...config, model: hasPresetModel ? "" : configuredModel });
              return;
            }
            onChange({ ...config, model: value });
          }}
        >
          <SelectTrigger className="mt-1" data-testid="select-model">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {models.map((m) => (
              <SelectItem key={m.value} value={m.value}>
                {m.label}
              </SelectItem>
            ))}
            <SelectItem value={customModelValue}>Custom model ID</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Custom Model ID (optional)</Label>
        <Input
          value={hasPresetModel ? "" : configuredModel}
          onChange={(e) => onChange({ ...config, model: e.target.value })}
          placeholder={customModelPlaceholder}
          className="mt-1"
          data-testid="input-custom-model-id"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Use this when the provider released a newer model not listed in the dropdown.
        </p>
      </div>

      {supportsReasoning && (
        <div>
          <Label>Reasoning Effort</Label>
          <Select
            value={selectedReasoning}
            onValueChange={(value: string) =>
              onChange({ ...config, reasoningEffort: value as NodeConfig["reasoningEffort"] })
            }
          >
            <SelectTrigger className="mt-1" data-testid="select-reasoning-effort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label>System Prompt</Label>
        <Textarea
          value={config.systemPrompt || ""}
          onChange={(e) => onChange({ ...config, systemPrompt: e.target.value })}
          placeholder="Extra instructions for this step..."
          className="mt-1 min-h-[80px] text-sm"
          data-testid="textarea-system-prompt"
        />
      </div>

      <div>
        <Label>Timeout (seconds, 0 = no timeout, max 7200)</Label>
        <Input
          type="number"
          min={0}
          max={7200}
          value={config.timeout ?? 300}
          onChange={(e) => {
            const parsed = Number.parseInt(e.target.value, 10);
            onChange({ ...config, timeout: Number.isNaN(parsed) ? 300 : Math.min(Math.max(parsed, 0), 7200) });
          }}
          className="mt-1"
          data-testid="input-node-timeout"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Max 7200 seconds (2 hours). Use `0` only for steps that can run long by design.
        </p>
      </div>

      <div>
        <Label>Max Retries</Label>
        <Input
          type="number"
          min={0}
          max={10}
          value={config.maxRetries ?? 0}
          onChange={(e) => onChange({ ...config, maxRetries: parseInt(e.target.value) || 0 })}
          className="mt-1"
          data-testid="input-max-retries"
        />
      </div>
    </>
  );
}

// ── Node Skills Config ──

const SKILL_NODE_TYPES: NodeType[] = ["plan", "subtasks", "ui_concept", "code", "llm_review"];
const MAX_SKILLS_PER_NODE = 3;

type SkillType = "process" | "reference";
type CompileStatus = "pending" | "compiled" | "error";

interface SkillSummary {
  id: string;
  skill_id: string;
  slug: string;
  name: string;
  skill_type: SkillType;
  compile_status: CompileStatus;
  max_tokens_estimate: number | null;
  position: number;
}

interface AvailableSkill {
  id: string;
  name: string;
  slug: string;
  skill_type: SkillType;
  compile_status: CompileStatus;
  max_tokens_estimate: number | null;
  is_system: boolean;
}

function NodeSkillsConfig({
  workflowId,
  nodeId,
  workspaceId,
}: {
  workflowId: string;
  nodeId: string;
  workspaceId: number | null;
}) {
  const [boundSkills, setBoundSkills] = useState<SkillSummary[]>([]);
  const [availableSkills, setAvailableSkills] = useState<AvailableSkill[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const isEphemeral = workflowId.startsWith("wf-");

  // Fetch available skills once per workspace (not per node)
  useEffect(() => {
    if (isEphemeral || !workspaceId) return;
    let cancelled = false;
    api.get<AvailableSkill[]>(`/api/skills?workspace_id=${workspaceId}`)
      .then((available) => {
        if (!cancelled) setAvailableSkills(available.filter((s) => s.compile_status === "compiled"));
      })
      .catch((err) => {
        if (!cancelled) console.error("Failed to load available skills:", err);
      });
    return () => { cancelled = true; };
  }, [workspaceId, isEphemeral]);

  // Fetch bound skills per node
  useEffect(() => {
    if (isEphemeral || !workspaceId) return;
    let cancelled = false;
    setLoading(true);
    api.get<SkillSummary[]>(`/api/workflows/${workflowId}/nodes/${nodeId}/skills`)
      .then((bound) => {
        if (!cancelled) setBoundSkills(bound);
      })
      .catch((err) => {
        if (!cancelled) console.error("Failed to load node skills:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [workflowId, nodeId, workspaceId, isEphemeral]);

  const saveBindings = async (next: SkillSummary[], rollback: SkillSummary[]) => {
    setSaving(true);
    try {
      await api.put(`/api/workflows/${workflowId}/nodes/${nodeId}/skills`, {
        skills: next.map((s, i) => ({ skill_id: s.skill_id, position: i })),
      });
    } catch (err) {
      console.error("Failed to save skill bindings:", err);
      setBoundSkills(rollback);
    } finally {
      setSaving(false);
    }
  };

  const addSkill = (skill: AvailableSkill) => {
    if (boundSkills.length >= MAX_SKILLS_PER_NODE) return;
    if (boundSkills.some((b) => b.skill_id === skill.id)) return;

    const newBinding: SkillSummary = {
      id: `temp-${Date.now()}`,
      skill_id: skill.id,
      slug: skill.slug,
      name: skill.name,
      skill_type: skill.skill_type,
      compile_status: skill.compile_status,
      max_tokens_estimate: skill.max_tokens_estimate,
      position: boundSkills.length,
    };

    const prev = boundSkills;
    const next = [...prev, newBinding];
    setBoundSkills(next);
    setPopoverOpen(false);
    saveBindings(next, prev);
  };

  const removeSkill = (skillId: string) => {
    const prev = boundSkills;
    const next = prev.filter((s) => s.skill_id !== skillId);
    setBoundSkills(next);
    saveBindings(next, prev);
  };

  const unboundSkills = useMemo(
    () => availableSkills.filter((s) => !boundSkills.some((b) => b.skill_id === s.id)),
    [availableSkills, boundSkills],
  );

  if (isEphemeral) {
    return (
      <div className="space-y-2 rounded-md border border-border p-3">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-muted-foreground" />
          <Label>Skills</Label>
        </div>
        <p className="text-xs text-muted-foreground">
          Save the workflow first to configure skills.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-muted-foreground" />
          <Label>Skills ({boundSkills.length}/{MAX_SKILLS_PER_NODE})</Label>
        </div>
        {saving && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
          <Loader2 className="w-3 h-3 animate-spin" />
          Loading skills...
        </div>
      ) : (
        <>
          {boundSkills.length > 0 && (
            <div className="space-y-1">
              {boundSkills.map((skill) => (
                <div
                  key={skill.skill_id}
                  className="flex items-center justify-between rounded-md bg-muted/50 px-2 py-1.5 text-xs"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${
                        skill.compile_status === "compiled" ? "bg-green-500" : "bg-red-500"
                      }`}
                    />
                    <span className="truncate font-medium">{skill.name}</span>
                    <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0">
                      {skill.skill_type}
                    </Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive shrink-0"
                    onClick={() => removeSkill(skill.skill_id)}
                    disabled={saving}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {boundSkills.length < MAX_SKILLS_PER_NODE && (
            <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-between text-xs"
                  disabled={saving || unboundSkills.length === 0}
                >
                  {unboundSkills.length === 0 ? "No skills available" : "Add skill..."}
                  <ChevronsUpDown className="w-3 h-3 ml-1 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search skills..." className="h-9" />
                  <CommandList>
                    <CommandEmpty>No skills found.</CommandEmpty>
                    <CommandGroup>
                      {unboundSkills.map((skill) => (
                        <CommandItem
                          key={skill.id}
                          value={skill.name}
                          onSelect={() => addSkill(skill)}
                        >
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="truncate">{skill.name}</span>
                              <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0">
                                {skill.skill_type}
                              </Badge>
                            </div>
                            {skill.max_tokens_estimate && (
                              <span className="text-[10px] text-muted-foreground shrink-0 ml-2">
                                ~{skill.max_tokens_estimate}t
                              </span>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}

          {boundSkills.length === 0 && !loading && (
            <p className="text-[10px] text-muted-foreground">
              Skills inject instructions into the agent's system prompt.
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ── Node Config Panel ──

function NodeConfigPanel({
  node,
  workflow,
  onClose,
  onUpdate,
  onDelete,
  onDeleteConnection,
}: {
  node: WorkflowNode;
  workflow: Workflow;
  onClose: () => void;
  onUpdate: (updates: Partial<WorkflowNode>) => void;
  onDelete: () => void;
  onDeleteConnection: (connectionId: string) => void;
}) {
  const isAINode = AI_NODE_TYPES.includes(node.type);
  const isStartOrEnd = node.type === "start" || node.type === "end";
  const incomingConnections = workflow.connections.filter((c) => c.targetId === node.id);
  const outgoingConnections = workflow.connections.filter((c) => c.sourceId === node.id);
  const startFieldOptions = getStartFieldOptions(workflow.issueType);
  const requiredStartFields = getRequiredStartFields(node.config.startRequiredFields, workflow.issueType);

  const getNodeLabel = (nodeId: string) => {
    return workflow.nodes.find((n) => n.id === nodeId)?.label || nodeId;
  };

  const updateConfig = (configUpdates: NodeConfig) => {
    onUpdate({ config: configUpdates });
  };

  return (
    <aside className="w-80 bg-card border-l border-border flex flex-col" data-testid="panel-node-config">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="font-semibold">Node Configuration</h3>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close node configuration" data-testid="button-close-node-config">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {/* Common: Label + Type */}
          <div>
            <Label>Label</Label>
            <Input
              value={node.label}
              onChange={(e) => onUpdate({ label: e.target.value })}
              className="mt-1"
              data-testid="input-node-label"
            />
          </div>

          <div>
            <Label>Node Type</Label>
            <div className="mt-1 text-sm text-muted-foreground capitalize">
              {node.type.replace("_", " ")}
            </div>
          </div>

          {node.type === "start" && (
            <div className="space-y-3 rounded-md border border-border p-3">
              <div>
                <Label>Required Intake Fields</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  These fields are required when creating a task with this workflow. For GitHub projects, values are copied into the issue body.
                </p>
              </div>
              <div className="space-y-2">
                {startFieldOptions.map((field) => {
                  const checked = requiredStartFields.includes(field.key);
                  return (
                    <div key={field.key} className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{field.label}</p>
                        {field.description ? (
                          <p className="text-xs text-muted-foreground">{field.description}</p>
                        ) : null}
                      </div>
                      <Switch
                        checked={checked}
                        onCheckedChange={(enabled) => {
                          const next = new Set(requiredStartFields as StartFieldKey[]);
                          if (enabled) {
                            next.add(field.key);
                          } else {
                            next.delete(field.key);
                          }
                          updateConfig({
                            ...node.config,
                            startRequiredFields: Array.from(next),
                          });
                        }}
                        data-testid={`switch-start-required-${field.key}`}
                      />
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Active: {requiredStartFields.map((field) => getStartFieldLabel(field)).join(", ") || "None"}
              </p>
            </div>
          )}

          {/* AI nodes: provider / model / system prompt / timeout / retries */}
          {isAINode && (
            <AIProviderConfig
              config={node.config}
              onChange={updateConfig}
            />
          )}

          {/* Skill bindings for AI-driven nodes */}
          {SKILL_NODE_TYPES.includes(node.type) && (
            <NodeSkillsConfig
              workflowId={workflow.id}
              nodeId={node.id}
              workspaceId={typeof workflow.workspaceId === "number" ? workflow.workspaceId : null}
            />
          )}

          {/* Test node: custom test command + auto-advance */}
          {node.type === "test" && (
            <>
              <div>
                <Label>Test Command</Label>
                <Input
                  value={node.config.testCommand || ""}
                  onChange={(e) => updateConfig({ ...node.config, testCommand: e.target.value })}
                  placeholder="auto-detect: npm test / pytest / make test"
                  className="mt-1"
                  data-testid="input-test-command"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Leave empty to auto-detect
                </p>
              </div>
              <div className="flex items-center justify-between">
                <Label>Auto-advance on success</Label>
                <Switch
                  checked={node.config.autoAdvance !== false}
                  onCheckedChange={(checked) => updateConfig({ ...node.config, autoAdvance: checked })}
                  data-testid="switch-auto-advance"
                />
              </div>
            </>
          )}

          {/* Human review: approval required toggle */}
          {node.type === "human_review" && (
            <div className="flex items-center justify-between">
              <Label>Approval Required</Label>
              <Switch
                checked={node.config.humanApprovalRequired !== false}
                onCheckedChange={(checked) => updateConfig({ ...node.config, humanApprovalRequired: checked })}
                data-testid="switch-human-approval"
              />
            </div>
          )}

          {/* LLM review: pass/fail behavior + iteration controls */}
          {node.type === "llm_review" && (
            <>
              <div className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                Output is saved in workflow run logs as review verdict + feedback. On FAIL, this node can auto-fix and retry.
              </div>
              <div>
                <Label>Max Iterations</Label>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  value={node.config.llmReviewMaxIterations ?? 4}
                  onChange={(e) =>
                    updateConfig({
                      ...node.config,
                      llmReviewMaxIterations: Math.min(
                        10,
                        Math.max(1, parseInt(e.target.value) || 1)
                      ),
                    })
                  }
                  className="mt-1"
                  data-testid="input-llm-review-max-iterations"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Auto-fix Between Iterations</Label>
                <Switch
                  checked={node.config.llmReviewAutoFix !== false}
                  onCheckedChange={(checked) =>
                    updateConfig({ ...node.config, llmReviewAutoFix: checked })
                  }
                  data-testid="switch-llm-review-auto-fix"
                />
              </div>
              <div className="space-y-1">
                <Label>Review Target Node</Label>
                <select
                  className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-sm"
                  value={node.config.reviewTargetNodeId || ""}
                  onChange={(e) =>
                    updateConfig({ ...node.config, reviewTargetNodeId: e.target.value || undefined })
                  }
                  data-testid="select-review-target-node"
                >
                  <option value="">Auto (direct predecessor)</option>
                  {workflow.nodes
                    .filter(
                      (n) =>
                        n.id !== node.id &&
                        n.type !== "start" &&
                        n.type !== "end" &&
                        n.type !== "human_review" &&
                        n.type !== "llm_review"
                    )
                    .map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.label || n.type} ({n.type})
                      </option>
                    ))}
                </select>
                <p className="text-[10px] text-muted-foreground">
                  Which node to re-run when review fails
                </p>
              </div>
              <div className="flex items-center justify-between">
                <Label>Block Workflow on Final FAIL</Label>
                <Switch
                  checked={node.config.llmReviewBlockOnFail !== false}
                  onCheckedChange={(checked) =>
                    updateConfig({ ...node.config, llmReviewBlockOnFail: checked })
                  }
                  data-testid="switch-llm-review-block-on-fail"
                />
              </div>
            </>
          )}

          {/* Deploy: draft PR toggle */}
          {node.type === "deploy" && (
            <div className="flex items-center justify-between">
              <Label>Create Draft PR</Label>
              <Switch
                checked={node.config.createDraftPr !== false}
                onCheckedChange={(checked) => updateConfig({ ...node.config, createDraftPr: checked })}
                data-testid="switch-draft-pr"
              />
            </div>
          )}

          <div className="pt-4 border-t space-y-2">
            <div className="flex items-center justify-between">
              <Label>Connections</Label>
              {(incomingConnections.length > 0 || outgoingConnections.length > 0) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    [...incomingConnections, ...outgoingConnections].forEach((c) =>
                      onDeleteConnection(c.id)
                    );
                  }}
                  data-testid="button-disconnect-all"
                >
                  Disconnect All
                </Button>
              )}
            </div>
            {incomingConnections.length === 0 && outgoingConnections.length === 0 ? (
              <p className="text-xs text-muted-foreground">No connections yet.</p>
            ) : (
              <div className="space-y-2">
                {incomingConnections.map((conn) => (
                  <div
                    key={conn.id}
                    className="flex items-center justify-between rounded-md border border-border p-2 text-xs"
                  >
                    <span className="truncate text-muted-foreground">From: {getNodeLabel(conn.sourceId)}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-destructive"
                      onClick={() => onDeleteConnection(conn.id)}
                      data-testid={`button-disconnect-${conn.id}`}
                    >
                      Disconnect
                    </Button>
                  </div>
                ))}
                {outgoingConnections.map((conn) => (
                  <div
                    key={conn.id}
                    className="flex items-center justify-between rounded-md border border-border p-2 text-xs"
                  >
                    <span className="truncate text-muted-foreground">To: {getNodeLabel(conn.targetId)}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-destructive"
                      onClick={() => onDeleteConnection(conn.id)}
                      data-testid={`button-disconnect-${conn.id}`}
                    >
                      Disconnect
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Delete button (not for start/end) */}
          {!isStartOrEnd && (
            <div className="pt-4 border-t">
              <Button
                variant="destructive"
                size="sm"
                className="w-full"
                onClick={onDelete}
                data-testid="button-delete-node"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete Node
              </Button>
            </div>
          )}
        </div>
      </ScrollArea>
    </aside>
  );
}

export default function AgentBuilder() {
  const { theme, toggleTheme } = useUIStore();
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const {
    workflows,
    selectedWorkflowId,
    selectedNodeId,
    isLoading,
    isSaving,
    selectWorkflow,
    selectNode,
    addWorkflow,
    updateWorkflow,
    deleteWorkflow,
    duplicateWorkflow,
    saveWorkflow,
    fetchWorkflows,
    addNode,
    updateNode,
    deleteNode,
    moveNode,
    addConnection,
    deleteConnection,
  } = useWorkflowStore();

  const [draggedNodeType, setDraggedNodeType] = useState<NodeType | null>(null);
  const [connectingFrom, setConnectingFrom] = useState<string | null>(null);
  const [camera, setCamera] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [draggingNode, setDraggingNode] = useState<{
    nodeId: string;
    startClientX: number;
    startClientY: number;
    startNodeX: number;
    startNodeY: number;
  } | null>(null);
  const didDragNodeRef = useRef(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedWorkflow = workflows.find((wf) => wf.id === selectedWorkflowId);
  const selectedNode = selectedWorkflow?.nodes.find((n) => n.id === selectedNodeId);

  // Fetch workflows from API on mount
  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows, activeWorkspaceId]);

  useEffect(() => {
    if (!selectedWorkflowId && workflows.length > 0) {
      selectWorkflow(workflows[0].id);
    }
  }, [selectedWorkflowId, workflows, selectWorkflow]);

  const drawWorkflow = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || !selectedWorkflow) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    const isDark = theme === "dark";
    ctx.fillStyle = isDark ? "#0f172a" : "#f8fafc";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.translate(camera.x, camera.y);
    ctx.scale(zoom, zoom);

    const gridSize = 20;
    const gridExtent = 2000;
    ctx.strokeStyle = isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)";
    ctx.lineWidth = 1 / zoom;
    for (let x = -gridExtent; x < gridExtent; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, -gridExtent);
      ctx.lineTo(x, gridExtent);
      ctx.stroke();
    }
    for (let y = -gridExtent; y < gridExtent; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(-gridExtent, y);
      ctx.lineTo(gridExtent, y);
      ctx.stroke();
    }

    selectedWorkflow.connections.forEach((conn) => {
      const sourceNode = selectedWorkflow.nodes.find((n) => n.id === conn.sourceId);
      const targetNode = selectedWorkflow.nodes.find((n) => n.id === conn.targetId);
      if (!sourceNode || !targetNode) return;

      const sourceX = sourceNode.x + 60;
      const sourceY = sourceNode.y + 30;
      const targetX = targetNode.x;
      const targetY = targetNode.y + 30;

      ctx.strokeStyle = isDark ? "#475569" : "#94a3b8";
      ctx.lineWidth = 2 / zoom;
      ctx.beginPath();
      ctx.moveTo(sourceX, sourceY);
      const midX = (sourceX + targetX) / 2;
      ctx.bezierCurveTo(midX, sourceY, midX, targetY, targetX, targetY);
      ctx.stroke();

      const arrowSize = 8;
      const angle = Math.atan2(targetY - sourceY, targetX - midX);
      ctx.fillStyle = isDark ? "#475569" : "#94a3b8";
      ctx.beginPath();
      ctx.moveTo(targetX, targetY);
      ctx.lineTo(targetX - arrowSize * Math.cos(angle - Math.PI / 6), targetY - arrowSize * Math.sin(angle - Math.PI / 6));
      ctx.lineTo(targetX - arrowSize * Math.cos(angle + Math.PI / 6), targetY - arrowSize * Math.sin(angle + Math.PI / 6));
      ctx.closePath();
      ctx.fill();
    });

    ctx.restore();
  }, [selectedWorkflow, theme, camera, zoom]);

  useEffect(() => {
    drawWorkflow();
    const handleResize = () => drawWorkflow();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [drawWorkflow]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.min(Math.max(zoom * delta, 0.25), 2);
      
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      const worldX = (mouseX - camera.x) / zoom;
      const worldY = (mouseY - camera.y) / zoom;
      
      setCamera({
        x: mouseX - worldX * newZoom,
        y: mouseY - worldY * newZoom,
      });
      
      setZoom(newZoom);
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
  }, [camera, zoom]);

  const handleCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedNodeType || !selectedWorkflowId || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - camera.x) / zoom - 60;
    const y = (e.clientY - rect.top - camera.y) / zoom - 30;

    const template = nodeTemplates.find((t) => t.type === draggedNodeType);
    if (!template) return;

    addNode(selectedWorkflowId, {
      type: draggedNodeType,
      label: template.label,
      x,
      y,
      config: { ...template.defaultConfig },
    });

    setDraggedNodeType(null);
  };

  const handleNodeClick = (nodeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (didDragNodeRef.current) {
      didDragNodeRef.current = false;
      return;
    }
    if (connectingFrom) {
      if (connectingFrom !== nodeId && selectedWorkflowId) {
        addConnection(selectedWorkflowId, connectingFrom, nodeId);
      }
      setConnectingFrom(null);
    } else {
      selectNode(nodeId);
    }
  };

  const handleStartConnection = (nodeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setConnectingFrom(nodeId);
  };

  const handleCreateWorkflow = (issueType: IssueType) => {
    const config = issueTypeConfig[issueType];
    addWorkflow({
      name: `New ${config.label} Workflow`,
      description: `Custom workflow for ${config.label.toLowerCase()}`,
      issueType,
      nodes: [
        { id: "n1", type: "start", label: "Start", x: 100, y: 200, config: {} },
        { id: "n2", type: "end", label: "End", x: 300, y: 200, config: {} },
      ],
      connections: [{ id: "c1", sourceId: "n1", targetId: "n2" }],
      isDefault: false,
    });
  };

  const handleAddNodeFromPalette = (nodeType: NodeType) => {
    if (!selectedWorkflowId || !selectedWorkflow) return;
    
    const template = nodeTemplates.find((t) => t.type === nodeType);
    if (!template) return;

    const existingNodes = selectedWorkflow.nodes;
    const maxX = existingNodes.length > 0 
      ? Math.max(...existingNodes.map(n => n.x)) + 150 
      : 100;
    const avgY = existingNodes.length > 0
      ? existingNodes.reduce((sum, n) => sum + n.y, 0) / existingNodes.length
      : 200;

    addNode(selectedWorkflowId, {
      type: nodeType,
      label: template.label,
      x: maxX,
      y: avgY,
      config: { ...template.defaultConfig },
    });
  };

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (e.button === 0 || e.button === 1) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - camera.x, y: e.clientY - camera.y });
    }
  };

  const handleNodeMouseDown = (nodeId: string, e: React.MouseEvent) => {
    if (e.button !== 0 || !selectedWorkflowId || !selectedWorkflow) return;
    const node = selectedWorkflow.nodes.find((n) => n.id === nodeId);
    if (!node) return;

    e.preventDefault();
    e.stopPropagation();
    didDragNodeRef.current = false;
    setDraggingNode({
      nodeId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startNodeX: node.x,
      startNodeY: node.y,
    });
  };

  const handleCanvasMouseMove = (e: React.MouseEvent) => {
    if (draggingNode && selectedWorkflowId) {
      const dx = (e.clientX - draggingNode.startClientX) / zoom;
      const dy = (e.clientY - draggingNode.startClientY) / zoom;
      if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
        didDragNodeRef.current = true;
      }
      moveNode(
        selectedWorkflowId,
        draggingNode.nodeId,
        draggingNode.startNodeX + dx,
        draggingNode.startNodeY + dy
      );
      return;
    }

    if (isPanning) {
      setCamera({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
    }
  };

  const handleCanvasMouseUp = () => {
    setIsPanning(false);
    setDraggingNode(null);
  };

  const handleResetView = () => {
    setCamera({ x: 0, y: 0 });
    setZoom(1);
  };

  return (
    <div className="h-full flex flex-col bg-background" data-testid="page-agent-builder">
      <header className="h-14 bg-card border-b border-border flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center gap-2">
          <WorkflowIcon className="w-5 h-5 text-muted-foreground" />
          <span className="font-semibold">Agent Builder</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"} data-testid="button-toggle-theme">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <aside className="w-64 bg-card border-r border-border flex flex-col min-h-0">
          <div className="p-4 border-b border-border">
            <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
              Workflows
            </div>
            <ScrollArea className="h-48">
              <div className="space-y-1 pr-2">
                {workflows.map((wf) => {
                  const issueConfig = issueTypeConfig[wf.issueType];
                  return (
                    <button
                      key={wf.id}
                      onClick={() => selectWorkflow(wf.id)}
                      className={`w-full flex items-center gap-2 p-2 rounded-md text-left text-sm transition-colors ${
                        selectedWorkflowId === wf.id
                          ? "bg-accent text-accent-foreground"
                          : "hover-elevate"
                      }`}
                      data-testid={`button-workflow-${wf.id}`}
                    >
                      <div
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: issueConfig.color }}
                      />
                      <span className="flex-1 truncate">{wf.name}</span>
                      {wf.isDefault && (
                        <Badge variant="outline" className="text-[10px] px-1">
                          Default
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          <div className="p-4 border-b border-border">
            <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
              New Workflow
            </div>
            <div className="grid grid-cols-2 gap-2">
              {(Object.keys(issueTypeConfig) as IssueType[]).map((type) => {
                const config = issueTypeConfig[type];
                return (
                  <Button
                    key={type}
                    variant="outline"
                    size="sm"
                    className="justify-start gap-1.5 text-xs"
                    onClick={() => handleCreateWorkflow(type)}
                    data-testid={`button-create-workflow-${type}`}
                  >
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.color }} />
                    {config.label}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="p-4 flex-1 min-h-0 flex flex-col">
            <div className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
              Node Palette
            </div>
            <ScrollArea className="flex-1 min-h-0">
              <div className="space-y-2 pr-2">
                {nodeTemplates.map((template) => {
                  const Icon = nodeIcons[template.type];
                  return (
                    <div
                      key={template.type}
                      draggable
                      onDragStart={(e) => {
                        setDraggedNodeType(template.type);
                        e.dataTransfer.effectAllowed = "copy";
                        e.dataTransfer.setData("text/plain", template.type);
                      }}
                      onDragEnd={() => setDraggedNodeType(null)}
                      onClick={() => handleAddNodeFromPalette(template.type)}
                      className="flex items-center gap-3 p-2 rounded-md border border-border bg-background cursor-pointer hover-elevate"
                      data-testid={`palette-node-${template.type}`}
                    >
                      <div
                        className="w-8 h-8 rounded-md flex items-center justify-center"
                        style={{ backgroundColor: `${template.color}20` }}
                      >
                        <Icon className="w-4 h-4" style={{ color: template.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{template.label}</div>
                        <div className="text-xs text-muted-foreground truncate">{template.description}</div>
                      </div>
                      <Plus className="w-4 h-4 text-muted-foreground" />
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        </aside>

        <main className="flex-1 flex flex-col overflow-hidden">
          {selectedWorkflow ? (
            <>
              <div className="h-12 bg-muted/30 border-b border-border flex items-center justify-between px-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: issueTypeConfig[selectedWorkflow.issueType].color }}
                  />
                  <Input
                    value={selectedWorkflow.name}
                    onChange={(e) => updateWorkflow(selectedWorkflow.id, { name: e.target.value })}
                    className="bg-transparent border-none text-lg font-medium w-64 p-0 h-auto focus-visible:ring-0"
                    data-testid="input-workflow-name"
                  />
                  <Badge variant="outline">{issueTypeConfig[selectedWorkflow.issueType].label}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => saveWorkflow(selectedWorkflow.id)}
                    disabled={isSaving}
                    data-testid="button-save-workflow"
                  >
                    {isSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    {isSaving ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => duplicateWorkflow(selectedWorkflow.id)}
                    data-testid="button-duplicate-workflow"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Duplicate
                  </Button>
                  {!selectedWorkflow.isDefault && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive"
                      onClick={() => deleteWorkflow(selectedWorkflow.id)}
                      data-testid="button-delete-workflow"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>

              <div
                ref={containerRef}
                className={`flex-1 relative overflow-hidden ${isPanning ? "cursor-grabbing" : "cursor-grab"}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleCanvasDrop}
                onMouseDown={handleCanvasMouseDown}
                onMouseMove={handleCanvasMouseMove}
                onMouseUp={handleCanvasMouseUp}
                onMouseLeave={handleCanvasMouseUp}
                onClick={() => {
                  if (!isPanning) {
                    selectNode(null);
                    setConnectingFrom(null);
                  }
                }}
              >
                <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />
                
                <div 
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    transform: `translate(${camera.x}px, ${camera.y}px) scale(${zoom})`,
                    transformOrigin: "0 0",
                  }}
                >
                  {selectedWorkflow.nodes.map((node) => {
                    const template = nodeTemplates.find((t) => t.type === node.type);
                    const Icon = nodeIcons[node.type];
                    const isSelected = selectedNodeId === node.id;
                    const isConnecting = connectingFrom === node.id;

                    return (
                      <div
                        key={node.id}
                        className={`absolute pointer-events-auto cursor-pointer transition-all ${
                          isSelected ? "ring-2 ring-primary ring-offset-2" : ""
                        } ${isConnecting ? "ring-2 ring-blue-500" : ""}`}
                        style={{
                          left: node.x,
                          top: node.y,
                          transform: isSelected ? "scale(1.05)" : "scale(1)",
                        }}
                        onMouseDown={(e) => handleNodeMouseDown(node.id, e)}
                        onClick={(e) => handleNodeClick(node.id, e)}
                        data-testid={`workflow-node-${node.id}`}
                    >
                      <Card className="w-[120px] shadow-lg overflow-visible">
                        <div
                          className="h-2 rounded-t-lg"
                          style={{ backgroundColor: template?.color || "#64748b" }}
                        />
                        <CardContent className="p-3">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: `${template?.color || "#64748b"}20` }}
                            >
                              <Icon className="w-4 h-4" style={{ color: template?.color || "#64748b" }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium truncate">{node.label}</div>
                            </div>
                          </div>
                        </CardContent>
                        <button
                          className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-primary flex items-center justify-center hover:scale-110 transition-transform"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => handleStartConnection(node.id, e)}
                          data-testid={`button-connect-${node.id}`}
                        >
                          <Plus className="w-3 h-3 text-primary-foreground" />
                        </button>
                        </Card>
                      </div>
                    );
                  })}
                </div>

                {connectingFrom && (
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-3 py-1 rounded-full text-sm z-10">
                    Click another node to connect
                  </div>
                )}

                <div className="absolute bottom-4 right-4 flex items-center gap-2 z-10">
                  <Badge variant="outline" className="text-xs">
                    {Math.round(zoom * 100)}%
                  </Badge>
                  <Button variant="outline" size="sm" onClick={handleResetView} data-testid="button-reset-view">
                    Reset View
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              Select a workflow from the sidebar to edit
            </div>
          )}
        </main>

        {selectedNode && selectedWorkflow && (
          <NodeConfigPanel
            node={selectedNode}
            workflow={selectedWorkflow}
            onClose={() => selectNode(null)}
            onUpdate={(updates) => updateNode(selectedWorkflow.id, selectedNode.id, updates)}
            onDelete={() => deleteNode(selectedWorkflow.id, selectedNode.id)}
            onDeleteConnection={(connectionId) => deleteConnection(selectedWorkflow.id, connectionId)}
          />
        )}
      </div>
    </div>
  );
}
